import { UploadOutlined } from "@ant-design/icons";
import { Button, notification } from "antd";
import { useImportInvoiceNamesMutation } from "api/sales-order/invoice-name-api";
import { GenderChicken } from "pages/sales-order/components/SalesOrderModalV2";
import { ChangeEvent, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { InvoiceNameForm } from "../types";

const EXCEL_COLUMNS = {
	itemCode: "Mã sản phẩm",
	itemName: "Tên Tồn Kho hệ thống",
	chickenType: "Loại gà",
	gender: "Giới tính",
	invoiceName: "Tên xuất hóa đơn",
} as const;

const OPTIONAL_EXCEL_COLUMNS = {
	deliveryNoteName: "Tên trên PXK",
} as const;

const GENDER_OPTIONS = Object.keys(GenderChicken);

const normalizeText = (value: unknown) => String(value || "").trim();

const parseChickenType = (value: unknown) => {
	const parsed = Number(normalizeText(value));

	return Number.isNaN(parsed) ? 0 : parsed;
};

const ImportExcelInvoiceNameButton = ({ disabled = false, setFailed }: { disabled?: boolean; setFailed: React.Dispatch<React.SetStateAction<string[]>> }) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [importInvoiceNames] = useImportInvoiceNamesMutation();
	const [loading, setLoading] = useState(false);

	const importExcel = async (file: File) => {
		const buffer = await file.arrayBuffer();
		const workbook = XLSX.read(buffer, { type: "array", cellDates: false, cellText: false });
		const sheetName = workbook.SheetNames[0];

		if (!sheetName) {
			throw new Error("File Excel không có dữ liệu.");
		}

		const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[sheetName], {
			defval: "",
		});

		if (!rows.length) {
			throw new Error("File Excel không có dữ liệu.");
		}

		console.log(rows);

		const missingColumns = Object.values(EXCEL_COLUMNS).filter((column) => !(column in rows[0]));

		if (missingColumns.length) {
			throw new Error(`Thiếu cột: ${missingColumns.join(", ")}`);
		}

		const payloads: InvoiceNameForm[] = rows
			.map((row) => ({
				itemCode: normalizeText(row[EXCEL_COLUMNS.itemCode]),
				itemName: normalizeText(row[EXCEL_COLUMNS.itemName]),
				chickenType: parseChickenType(row[EXCEL_COLUMNS.chickenType]),
				gender: normalizeText(row[EXCEL_COLUMNS.gender]),
				invoiceName: normalizeText(row[EXCEL_COLUMNS.invoiceName]),
				deliveryNoteName: normalizeText(row[OPTIONAL_EXCEL_COLUMNS.deliveryNoteName]) || undefined,
				isActive: true,
			}))
			.filter((row) => row.itemCode || row.itemName || row.invoiceName);

		if (!payloads.length) {
			throw new Error("Không tìm thấy dòng dữ liệu hợp lệ.");
		}

		const invalidGender = payloads.find((row) => row.gender && !GENDER_OPTIONS.includes(row.gender));

		if (invalidGender) {
			throw new Error(`Giới tính không hợp lệ: ${invalidGender.gender}`);
		}

		const invalidChickenType = payloads.find((row) => !row.chickenType || row.chickenType < 1 || row.chickenType > 4);

		if (invalidChickenType) {
			throw new Error(`Loại gà không hợp lệ tại mã sản phẩm: ${invalidChickenType.itemCode || "N/A"}`);
		}

		const missingRequiredValue = payloads.find((row) => !row.itemCode || !row.itemName || !row.gender || !row.invoiceName);

		if (missingRequiredValue) {
			throw new Error(`Thiếu dữ liệu bắt buộc tại mã sản phẩm: ${missingRequiredValue.itemCode || "N/A"}`);
		}

		return await importInvoiceNames(payloads).unwrap();
	};

	const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];

		if (!file) return;

		setLoading(true);

		try {
			const result = await importExcel(file);

			notification.success({
				message: `Import thành công ${result.success} dòng`,
			});
			if (result.failed.length) {
				setFailed(result.failed);
			}
		} catch (error: any) {
			notification.error({
				message: error?.data?.message || error?.message || "Import thất bại.",
			});
		} finally {
			setLoading(false);
			event.target.value = "";
		}
	};

	return (
		<>
			<Button disabled={disabled} loading={loading} type="primary" ghost icon={<UploadOutlined />} onClick={() => inputRef.current?.click()}>
				Import Excel
			</Button>
			<input ref={inputRef} style={{ display: "none" }} type="file" accept=".xlsx,.xls" onChange={handleChange} />
		</>
	);
};

export default ImportExcelInvoiceNameButton;
